/**
 * Music Studio public showcase — anonymous marketing landing at /music-showcase.
 *
 * Goal: convert curious prospects into trial signups by SHOWING them what the
 * Music Studio Prompt Builder produces for every broadcaster scenario.
 * The 16 templates fan out as cards, each revealing the AI-composed Suno
 * prompt + suggested tags. CTAs throughout drive to /login → trial.
 *
 * Cost model: ZERO ongoing LLM cost. The backend caches each composed prompt
 * to MongoDB after the first hit, so the page is static-fast forever.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Music, Sparkles, Loader2, ArrowRight, Disc3, Volume2,
  Mic, Newspaper, Trophy, Cloud, Car, MessageCircle, Headphones,
  Megaphone, PartyPopper, Gift, Share2, Youtube, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// The Music Studio CTA on this page should drop the visitor straight into
// the Music Studio after they sign in (or open it directly if already
// authenticated). Use a shared destination + a helper that picks between
// "deep-link to the dashboard page" and "send to login with a ?next=…".
const POST_LOGIN_DEST = "/dashboard/music-studio";
const TRIAL_CTA_HREF_ANON = `/login?signup=1&next=${encodeURIComponent(POST_LOGIN_DEST)}`;


// Category → icon (purely visual)
const CATEGORY_ICONS = {
  imaging:    Disc3,
  podcast:    Mic,
  commercial: Megaphone,
  seasonal:   Gift,
  social:     Share2,
};

// Template id → specific icon (overrides category default for richer cards)
const TEMPLATE_ICONS = {
  station_theme:     Music,
  morning_theme:     Sparkles,
  afternoon_theme:   Volume2,
  evening_theme:     Headphones,
  news_bed:          Newspaper,
  sports_bed:        Trophy,
  weather_bed:       Cloud,
  traffic_bed:       Car,
  talk_show_bed:     MessageCircle,
  podcast_theme:     Mic,
  commercial_bed:    Megaphone,
  holiday_music:     Gift,
  contest_theme:     PartyPopper,
  sponsor_music:     Music,
  social_short:      Share2,
  youtube_background: Youtube,
};

const CATEGORY_LABELS = {
  imaging:    "Station Imaging",
  podcast:    "Podcast",
  commercial: "Commercial & Sponsor",
  seasonal:   "Seasonal",
  social:     "Social & Video",
};


export default function MusicShowcase() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/music-studio/showcase");
        setGroups(r.data?.groups || []);
      } catch {
        setGroups([]);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-bg text-ink-primary" data-testid="music-showcase">
      <ShowcaseHeader />
      <Hero />
      <main className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="py-32 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin text-brand-amber" />
            <span>Loading the BroadcastFlow template library…</span>
          </div>
        ) : (groups || []).length === 0 ? (
          <div className="py-32 text-center text-ink-muted">Couldn&apos;t load the showcase right now — try again in a moment.</div>
        ) : (
          <>
            {groups.map((g) => (
              <CategorySection key={g.category} category={g.category} items={g.items} />
            ))}
            <BottomCTA />
          </>
        )}
      </main>
      <ShowcaseFooter />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Header — minimal, links back to site + Login + Start Trial
// ──────────────────────────────────────────────────────────────
function ShowcaseHeader() {
  const { user } = useAuth();
  const ctaHref = user ? POST_LOGIN_DEST : TRIAL_CTA_HREF_ANON;
  const ctaLabel = user ? "Open AI Music Studio" : "Start free trial";
  return (
    <header className="sticky top-0 z-30 bg-bg/85 backdrop-blur-md border-b border-line">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-home">
          <Music className="h-5 w-5 text-brand-amber" />
          <span className="font-bold tracking-tight">BroadcastFlow</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted ml-1">// AI Music Studio</span>
        </Link>
        <div className="flex items-center gap-3">
          {!user && (
            <Link to="/login" className="text-sm text-ink-secondary hover:text-brand-amber" data-testid="header-login">Login</Link>
          )}
          <Link to={ctaHref}
            data-testid="header-cta"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand-amber text-bg font-bold amber-glow">
            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}


// ──────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────
function Hero() {
  const { user } = useAuth();
  const ctaHref = user ? POST_LOGIN_DEST : TRIAL_CTA_HREF_ANON;
  const ctaLabel = user ? "Open AI Music Studio" : "Start your free trial";
  return (
    <section className="relative overflow-hidden border-b border-line">
      {/* Ambient gradient layer */}
      <div className="absolute inset-0 -z-0 opacity-30"
           style={{ background: "radial-gradient(ellipse at top, rgba(255,176,32,0.25), transparent 60%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-amber/40 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-brand-amber border border-brand-amber/40 bg-brand-amber/10 px-3 py-1 rounded-full mb-6">
          <Sparkles className="h-3 w-3" />
          BroadcastFlow AI Music Studio · NEW
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight max-w-3xl mx-auto">
          AI-produced station music
          <br />
          <span className="text-brand-amber">tailored to every broadcast moment.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-ink-secondary max-w-2xl mx-auto leading-relaxed">
          16 broadcaster templates, one optimised Suno prompt each, generated on demand.
          From morning show themes to news beds to YouTube background loops — your music
          identity is one click away.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to={ctaHref} data-testid="hero-cta"
            className="inline-flex items-center gap-2 text-base px-5 py-3 rounded-md bg-brand-amber text-bg font-bold amber-glow">
            {ctaLabel} <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="#templates" data-testid="hero-browse"
            className="inline-flex items-center gap-2 text-sm px-4 py-3 rounded-md border border-line hover:border-brand-amber/40 text-ink-primary">
            Browse the 16 templates ↓
          </a>
        </div>
        <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-ink-muted">
          <li className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-amber" /> BYOK Suno · no platform markup</li>
          <li className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-amber" /> AI Prompt Builder included</li>
          <li className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-amber" /> Library · Favorites · Collections</li>
          <li className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-amber" /> Sends to Imaging · Podcast · Vision</li>
        </ul>
      </div>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────
// Category section (e.g. Station Imaging, Podcast, etc.)
// ──────────────────────────────────────────────────────────────
function CategorySection({ category, items }) {
  const CatIcon = CATEGORY_ICONS[category] || Music;
  return (
    <section id="templates" className="pt-16" data-testid={`category-${category}`}>
      <div className="flex items-center gap-2 mb-5">
        <CatIcon className="h-5 w-5 text-brand-amber" />
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
          {CATEGORY_LABELS[category] || category}
        </h2>
        <span className="text-[11px] font-mono text-ink-muted">{items.length} templates</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => <TemplateCard key={t.template_id} t={t} />)}
      </div>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────
// Template card — the showcase money-maker
// ──────────────────────────────────────────────────────────────
function TemplateCard({ t }) {
  const Icon = TEMPLATE_ICONS[t.template_id] || Music;
  const defaults = t.defaults || {};
  const chips = useMemo(() => {
    const xs = [];
    if (defaults.genre) xs.push(defaults.genre);
    if (defaults.mood) xs.push(defaults.mood);
    if (defaults.energy) xs.push(`${defaults.energy} energy`);
    if (defaults.tempo) xs.push(defaults.tempo);
    if (defaults.instrumental) xs.push("instrumental");
    if (defaults.vocal_style && !defaults.instrumental) xs.push(`${defaults.vocal_style} vocals`);
    if (defaults.length) xs.push(defaults.length);
    return xs;
  }, [defaults]);

  return (
    <article
      className="group relative rounded-lg border border-line bg-bg-elevated hover:border-brand-amber/40 transition-colors overflow-hidden"
      data-testid={`tpl-card-${t.template_id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-brand-amber/10 border border-brand-amber/30 text-brand-amber">
            <Icon className="h-5 w-5" />
          </div>
          <span className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">{t.category}</span>
        </div>
        <h3 className="text-base font-bold mb-1 leading-tight">{t.label}</h3>
        <p className="text-xs text-ink-secondary leading-relaxed mb-3 line-clamp-2">{t.purpose}</p>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {chips.slice(0, 5).map((c, i) => (
              <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border border-line text-ink-muted">
                {c}
              </span>
            ))}
          </div>
        )}

        <div className="rounded-md bg-bg border border-brand-amber/15 p-3 mb-3">
          <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider text-brand-amber mb-1.5">
            <Sparkles className="h-2.5 w-2.5" />
            AI-composed Suno prompt
          </div>
          <p className="text-[11px] text-ink-primary font-mono leading-relaxed line-clamp-3" data-testid={`tpl-prompt-${t.template_id}`}>
            {t.composed_prompt || t.purpose}
          </p>
          {(t.composed_tags || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {t.composed_tags.slice(0, 4).map((tg, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded border border-brand-amber/30 text-brand-amber bg-brand-amber/5">
                  {tg}
                </span>
              ))}
            </div>
          )}
        </div>

        <Link
          to="/login?signup=1"
          data-testid={`tpl-cta-${t.template_id}`}
          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/10 transition"
        >
          Render this in your trial <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}


// ──────────────────────────────────────────────────────────────
// Bottom CTA
// ──────────────────────────────────────────────────────────────
function BottomCTA() {
  const { user } = useAuth();
  const ctaHref = user ? POST_LOGIN_DEST : TRIAL_CTA_HREF_ANON;
  const ctaLabel = user ? "Open AI Music Studio" : "Start your free trial";
  return (
    <section className="mt-24 rounded-xl border border-brand-amber/40 bg-gradient-to-br from-brand-amber/15 to-brand-amber/5 p-10 lg:p-14 text-center">
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">
        Stop licensing — start producing.
      </h2>
      <p className="text-base text-ink-secondary max-w-xl mx-auto mb-6 leading-relaxed">
        BroadcastFlow AI Music Studio is included in every Professional, Pro Radio, and Network plan.
        Start a free trial and render your station&apos;s signature themes in minutes.
      </p>
      <Link to={ctaHref} data-testid="bottom-cta"
        className="inline-flex items-center gap-2 text-base px-6 py-3 rounded-md bg-brand-amber text-bg font-bold amber-glow">
        {ctaLabel} <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────
// Footer
// ──────────────────────────────────────────────────────────────
function ShowcaseFooter() {
  return (
    <footer className="border-t border-line py-10 mt-10">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-muted">
        <div>© BroadcastFlow · AI Music Studio runs on your choice of Suno wrapper · BYOK</div>
        <div className="flex items-center gap-4">
          <Link to="/terms" className="hover:text-brand-amber">Terms</Link>
          <Link to="/privacy" className="hover:text-brand-amber">Privacy</Link>
          <Link to="/refunds" className="hover:text-brand-amber">Refunds</Link>
        </div>
      </div>
    </footer>
  );
}
